import React, { useState, useMemo } from "react";
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { ChevronDown, ChevronUp } from "lucide-react";

import PatientImageItem from "./PatientImageItem";
import {
  formatSurgeryStageLabel,
  SURGERY_STAGE_OPTIONS,
} from "./surgeryStageOptions";

const tokens = {
  color: {
    surface: "#FFFFFF",
    border: "#EEEEEE",
    text: "#333333",
    textMuted: "#777777",
    textSubtle: "#999999",
    icon: "#555555",
    loading: "#0085FF",
    accent: "#00B7F4",
    accentSoft: "#E6F8FE",
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    xxl: 24,
  },
  radius: {
    md: 8,
  },
  fontSize: {
    xs: 12,
    sm: 14,
    md: 16,
  },
  lineHeight: {
    sm: 20,
  },
};

const getStableImageId = (image) => String(image?.image_data?.id ?? "");

const getImageDateKey = (image) => {
  const metadata = image?.image_data || {};
  const dateValue = metadata.date_taken || metadata.created_at;

  if (!dateValue) {
    return "";
  }

  const dateString = String(dateValue);
  const isoDateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoDateMatch) {
    return `${isoDateMatch[1]}-${isoDateMatch[2]}-${isoDateMatch[3]}`;
  }

  const parsedDate = new Date(dateString);

  if (Number.isNaN(parsedDate.getTime())) {
    return dateString;
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDateLabel = (dateKey) => {
  const isoDateMatch = String(dateKey).match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!isoDateMatch) {
    return dateKey || "Recent photos";
  }

  const date = new Date(
    Number(isoDateMatch[1]),
    Number(isoDateMatch[2]) - 1,
    Number(isoDateMatch[3]),
  );

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const compareImageDatesDesc = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return b.localeCompare(a);
};

const sortImagesByViewType = (images) => {
  const order = { Front: 1, Right: 2, Left: 3, Back: 4 };

  return [...images].sort((a, b) => {
    if (!a.image_data || !b.image_data) return 0;

    const viewA = a.image_data.view_type_human_readable || "";
    const viewB = b.image_data.view_type_human_readable || "";
    const priorityA = order[viewA] || 5;
    const priorityB = order[viewB] || 5;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    const createdAtA = a.image_data.created_at || "";
    const createdAtB = b.image_data.created_at || "";

    if (createdAtA !== createdAtB) {
      return createdAtB.localeCompare(createdAtA);
    }

    return (b.image_data.id || 0) - (a.image_data.id || 0);
  });
};

/**
 * Component for displaying patient images organized by batches/stages
 */
const PatientImagesView = ({
  images = [],
  isLoading = false,
  renderImageItem,
  onImageTap,
  onSurgeryStageChange,
  updatingImageStageIds = {},
  session,
  trackEvent,
}) => {
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [selectedImageIds, setSelectedImageIds] = useState([]);
  const [focusedControl, setFocusedControl] = useState(null);

  const selectedImageIdSet = useMemo(
    () => new Set(selectedImageIds),
    [selectedImageIds],
  );

  // Group images first to initialize expanded state
  // Group images by batch_id, days_postop, or surgery_stage
  const organizedImageGroups = useMemo(() => {
    if (!images || images.length === 0) {
      return [];
    }

    // Create a map to group images
    const groupedImages = {};
    const otherImages = [];

    // First pass: Group by surgery_stage (new primary grouping)
    images.forEach((image) => {
      // Check if the image has the required structure
      if (!image.image_data) {
        otherImages.push(image);
        return;
      }

      const surgeryStage = image.image_data.surgery_stage;

      if (surgeryStage) {
        const key = `stage-${surgeryStage}`;
        const stageLabel = formatSurgeryStageLabel(surgeryStage);
        if (!groupedImages[key]) {
          groupedImages[key] = {
            id: key,
            title: `${stageLabel} Photos`,
            description: `${stageLabel} Photos`,
            images: [],
          };
        }
        groupedImages[key].images.push(image);
      } else {
        otherImages.push(image);
      }
    });

    // Second pass: Group remaining images by days_postop
    const remainingAfterDaysPostop = [];
    otherImages.forEach((image) => {
      if (!image.image_data) {
        remainingAfterDaysPostop.push(image);
        return;
      }

      const daysPostop = image.image_data.days_postop;

      if (daysPostop !== null && daysPostop !== undefined) {
        const key = `postop-${daysPostop}`;
        if (!groupedImages[key]) {
          groupedImages[key] = {
            id: key,
            title:
              daysPostop === 0
                ? "Immediately Post-Op"
                : `${daysPostop} Days Post-Op`,
            description: "",
            images: [],
          };
        }
        groupedImages[key].images.push(image);
      } else {
        remainingAfterDaysPostop.push(image);
      }
    });

    // Third pass: Group by batch_id
    const finalRemainingImages = [];
    remainingAfterDaysPostop.forEach((image) => {
      if (!image.image_data) {
        finalRemainingImages.push(image);
        return;
      }

      const batchId = image.image_data.batch_id;

      if (batchId) {
        const key = `batch-${batchId}`;
        if (!groupedImages[key]) {
          // Use surgery_stage for description if available - RESTORED BEHAVIOR
          const surgeryStage = image.image_data.surgery_stage;
          let description = "";

          if (surgeryStage) {
            description = `${formatSurgeryStageLabel(surgeryStage)} Photos`;
          } else {
            description = "Patient Photos";
          }

          groupedImages[key] = {
            id: key,
            title: `Photo Batch ${batchId}`,
            description: description,
            images: [],
          };
        }
        groupedImages[key].images.push(image);
      } else {
        finalRemainingImages.push(image);
      }
    });

    // Fourth pass: Put in "Other" bucket
    if (finalRemainingImages.length > 0) {
      if (!groupedImages["other"]) {
        groupedImages["other"] = {
          id: "other",
          title: "Other Photos",
          description: "",
          images: [],
        };
      }
      finalRemainingImages.forEach((image) => {
        groupedImages["other"].images.push(image);
      });
    }

    // Sort images within each group and create most-recent-first date groups.
    Object.values(groupedImages).forEach((group) => {
      const imagesByDate = {};

      group.images.forEach((image) => {
        const dateKey = getImageDateKey(image);

        if (!imagesByDate[dateKey]) {
          imagesByDate[dateKey] = {
            id: `${group.id}-date-${dateKey || "recent"}`,
            dateKey,
            title: formatDateLabel(dateKey),
            images: [],
          };
        }

        imagesByDate[dateKey].images.push(image);
      });

      group.dateGroups = Object.values(imagesByDate)
        .sort((a, b) => compareImageDatesDesc(a.dateKey, b.dateKey))
        .map((dateGroup) => ({
          ...dateGroup,
          images: sortImagesByViewType(dateGroup.images),
        }));
    });

    // Convert map to array and sort:
    // 1. Surgery stage groups first
    // 2. Then days postop groups
    // 3. Then batch groups
    // 4. Other at the end
    const result = Object.values(groupedImages).sort((a, b) => {
      // Determine group type
      const getGroupType = (id) => {
        if (id.startsWith("stage-")) return 1;
        if (id.startsWith("postop-")) return 2;
        if (id.startsWith("batch-")) return 3;
        return 4; // 'other'
      };

      const typeA = getGroupType(a.id);
      const typeB = getGroupType(b.id);

      // First sort by group type
      if (typeA !== typeB) {
        return typeA - typeB;
      }

      // For same group types, sort by specific logic
      if (typeA === 1) {
        // Surgery stages - sort alphabetically
        return a.title.localeCompare(b.title);
      } else if (typeA === 2) {
        // Days postop - sort numerically (ascending)
        const daysA = parseInt(a.id.replace("postop-", ""));
        const daysB = parseInt(b.id.replace("postop-", ""));
        return daysA - daysB;
      } else if (typeA === 3) {
        // Batch IDs - sort numerically (descending)
        const batchA = parseInt(a.id.replace("batch-", ""));
        const batchB = parseInt(b.id.replace("batch-", ""));
        return batchB - batchA; // Higher batch numbers first (most recent)
      }

      // Default for other categories
      return 0;
    });

    // Log the final organized groups
    return result;
  }, [images]);

  // State for expanded sections - initialize all sections as expanded
  const [expandedSections, setExpandedSections] = useState(() => {
    const initialExpanded = {};
    organizedImageGroups.forEach((group) => {
      initialExpanded[group.id] = true;
    });
    return initialExpanded;
  });

  // Update expanded sections when new groups are added
  React.useEffect(() => {
    setExpandedSections((prev) => {
      const newExpanded = { ...prev };
      organizedImageGroups.forEach((group) => {
        if (!(group.id in newExpanded)) {
          newExpanded[group.id] = true;
        }
      });
      return newExpanded;
    });
  }, [organizedImageGroups]);

  // Toggle a section's expanded state
  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));

    // Track event when a section is expanded/collapsed
    if (trackEvent && session) {
      trackEvent(
        expandedSections[sectionId]
          ? "Image Group Collapsed"
          : "Image Group Expanded",
        {
          "Group ID": sectionId,
          "View Mode": "planningView",
        },
        session,
      );
    }
  };

  const closeEditMode = () => {
    setEditingGroupId(null);
    setSelectedImageIds([]);
  };

  const toggleEditMode = (groupId) => {
    setEditingGroupId((currentGroupId) => {
      const nextGroupId = currentGroupId === groupId ? null : groupId;
      setSelectedImageIds([]);

      if (nextGroupId) {
        setExpandedSections((prev) => ({
          ...prev,
          [groupId]: true,
        }));
      }

      return nextGroupId;
    });
  };

  const toggleImageSelection = (image) => {
    const imageId = getStableImageId(image);
    if (!imageId) return;

    setSelectedImageIds((currentIds) =>
      currentIds.includes(imageId)
        ? currentIds.filter((currentId) => currentId !== imageId)
        : [...currentIds, imageId],
    );
  };

  const moveSelectedImagesToStage = async (group, nextStage) => {
    if (!onSurgeryStageChange || selectedImageIds.length === 0) return;

    const selectedImages = group.images.filter((image) =>
      selectedImageIdSet.has(getStableImageId(image)),
    );
    const imagesToUpdate = selectedImages.filter(
      (image) => image?.image_data?.surgery_stage !== nextStage,
    );

    if (imagesToUpdate.length === 0) {
      closeEditMode();
      return;
    }

    await onSurgeryStageChange(imagesToUpdate, nextStage);
    closeEditMode();
  };

  // Default render function for image items if not provided
  const defaultRenderImageItem = ({ item, index, isEditingGroup }) => {
    const imageId = item.image_data?.id;
    const isUpdatingStage = Boolean(updatingImageStageIds[imageId]);
    const stableImageId = getStableImageId(item);

    return (
      <PatientImageItem
        item={item}
        index={index}
        isEditing={isEditingGroup}
        isSelected={selectedImageIdSet.has(stableImageId)}
        isUpdatingStage={isUpdatingStage}
        onImageTap={onImageTap}
        onSelectionToggle={toggleImageSelection}
      />
    );
  };

  const renderPhotoItem = (item, index, isEditingGroup) =>
    renderImageItem
      ? renderImageItem({ item, index })
      : defaultRenderImageItem({ item, index, isEditingGroup });

  // If loading, show spinner
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={tokens.color.loading} />
        <Text style={styles.loadingText}>Loading patient images...</Text>
      </View>
    );
  }

  // If no images or groups, show empty state
  if (organizedImageGroups.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>
          No photos available for this patient
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {organizedImageGroups.map((group) => {
        const isEditingGroup = editingGroupId === group.id;
        const selectedCount = isEditingGroup ? selectedImageIds.length : 0;
        const isAnySelectedImageUpdating = selectedImageIds.some(
          (imageId) => updatingImageStageIds[imageId],
        );
        const groupStage = group.id.startsWith("stage-")
          ? group.id.replace("stage-", "")
          : null;
        const editButtonFocusKey = `edit-stage-${group.id}`;

        return (
          <View key={group.id} style={styles.groupContainer}>
            <View style={styles.groupHeader}>
              <TouchableOpacity
                style={styles.groupHeaderToggle}
                onPress={() => toggleSection(group.id)}
                accessibilityRole="button"
                accessibilityLabel={`${expandedSections[group.id] ? "Collapse" : "Expand"} ${group.description || group.title}`}
              >
                <View style={styles.groupHeaderContent}>
                  {group.description ? (
                    <Text style={styles.groupTitle}>{group.description}</Text>
                  ) : null}
                  <Text style={styles.imageCount}>
                    {group.images.length} photos
                  </Text>
                </View>
                <View style={styles.expandIcon}>
                  {expandedSections[group.id] ? (
                    <ChevronUp size={20} color={tokens.color.icon} />
                  ) : (
                    <ChevronDown size={20} color={tokens.color.icon} />
                  )}
                </View>
              </TouchableOpacity>

              {onSurgeryStageChange ? (
                <TouchableOpacity
                  style={[
                    styles.editStageButton,
                    isEditingGroup && styles.editStageButtonActive,
                    focusedControl === editButtonFocusKey &&
                      styles.focusedControl,
                  ]}
                  onPress={() => toggleEditMode(group.id)}
                  onFocus={() => setFocusedControl(editButtonFocusKey)}
                  onBlur={() => setFocusedControl(null)}
                  accessibilityRole="button"
                  accessibilityLabel={`${isEditingGroup ? "Cancel editing" : "Edit surgery stage"} for ${group.description || group.title}`}
                >
                  <Text
                    style={[
                      styles.editStageButtonText,
                      isEditingGroup && styles.editStageButtonTextActive,
                    ]}
                  >
                    {isEditingGroup ? "Cancel" : "Edit"}
                  </Text>
                </TouchableOpacity>
              ) : null}
            </View>

            {expandedSections[group.id] && (
              <View style={styles.groupContent}>
                {isEditingGroup ? (
                  <View style={styles.stageEditBar}>
                    <View style={styles.stageEditMeta}>
                      <Text style={styles.stageEditTitle}>
                        Edit Surgery Stage
                      </Text>
                      <Text style={styles.stageEditCount}>
                        {selectedCount} selected
                      </Text>
                    </View>
                    {selectedCount > 0 ? (
                      <View style={styles.stageOptions}>
                        {SURGERY_STAGE_OPTIONS.map((option) => {
                          const isCurrentGroupStage =
                            groupStage === option.value;
                          const optionFocusKey = `move-stage-${group.id}-${option.value}`;
                          const isDisabled =
                            isAnySelectedImageUpdating || isCurrentGroupStage;

                          return (
                            <TouchableOpacity
                              key={option.value}
                              style={[
                                styles.stageOptionButton,
                                isCurrentGroupStage &&
                                  styles.stageOptionButtonCurrent,
                                focusedControl === optionFocusKey &&
                                  styles.focusedControl,
                                isDisabled && styles.disabledControl,
                              ]}
                              onPress={() =>
                                moveSelectedImagesToStage(group, option.value)
                              }
                              disabled={isDisabled}
                              onFocus={() => setFocusedControl(optionFocusKey)}
                              onBlur={() => setFocusedControl(null)}
                              accessibilityRole="button"
                              accessibilityLabel={`Move selected photos to ${option.label}`}
                              accessibilityState={{ disabled: isDisabled }}
                            >
                              <Text style={styles.stageOptionButtonText}>
                                {option.label}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    ) : (
                      <Text style={styles.stageEditInstruction}>
                        Select the photos you want to move to a different
                        surgery stage.
                      </Text>
                    )}
                  </View>
                ) : null}

                {group.dateGroups.map((dateGroup) => (
                  <View key={dateGroup.id} style={styles.dateGroup}>
                    <View style={styles.dateHeader}>
                      <Text style={styles.dateTitle}>{dateGroup.title}</Text>
                      <Text style={styles.dateCount}>
                        {dateGroup.images.length} photos
                      </Text>
                    </View>
                    <View style={styles.imageGrid}>
                      {dateGroup.images.map((item, index) => (
                        <React.Fragment
                          key={
                            item.image_data?.id?.toString() ||
                            `${dateGroup.id}-${index}`
                          }
                        >
                          {renderPhotoItem(item, index, isEditingGroup)}
                        </React.Fragment>
                      ))}
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  loadingContainer: {
    padding: tokens.spacing.xxl,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 100,
  },
  loadingText: {
    fontSize: tokens.fontSize.md,
    color: tokens.color.icon,
    marginTop: tokens.spacing.md,
  },
  emptyContainer: {
    padding: tokens.spacing.xl,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    fontSize: tokens.fontSize.md,
    color: tokens.color.textMuted,
    marginBottom: tokens.spacing.lg,
    textAlign: "center",
  },
  groupContainer: {
    marginBottom: tokens.spacing.md,
    borderWidth: 1,
    borderColor: tokens.color.border,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.surface,
    overflow: "hidden",
  },
  groupHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: tokens.color.surface,
  },
  groupHeaderToggle: {
    flex: 1,
    minHeight: 56,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: tokens.spacing.lg,
  },
  groupHeaderContent: {
    flex: 1,
  },
  groupTitle: {
    fontSize: tokens.fontSize.md,
    fontWeight: "500",
    color: tokens.color.text,
  },
  groupDescription: {
    fontSize: tokens.fontSize.sm,
    color: tokens.color.textMuted,
    marginTop: 2,
  },
  imageCount: {
    fontSize: tokens.fontSize.xs,
    color: tokens.color.textSubtle,
    marginTop: tokens.spacing.xs,
  },
  expandIcon: {
    marginLeft: tokens.spacing.sm,
  },
  editStageButton: {
    minHeight: 44,
    minWidth: 64,
    paddingHorizontal: tokens.spacing.md,
    alignItems: "center",
    justifyContent: "center",
    borderLeftWidth: 1,
    borderLeftColor: tokens.color.border,
  },
  editStageButtonActive: {
    backgroundColor: tokens.color.accentSoft,
  },
  editStageButtonText: {
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
    color: tokens.color.text,
    fontWeight: "500",
  },
  editStageButtonTextActive: {
    color: tokens.color.accent,
  },
  groupContent: {
    borderTopWidth: 1,
    borderTopColor: tokens.color.border,
    padding: tokens.spacing.md,
    gap: tokens.spacing.lg,
  },
  stageEditBar: {
    gap: tokens.spacing.md,
    padding: tokens.spacing.md,
    borderWidth: 1,
    borderColor: tokens.color.border,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.surface,
  },
  stageEditMeta: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: tokens.spacing.md,
  },
  stageEditTitle: {
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
    fontWeight: "500",
    color: tokens.color.text,
  },
  stageEditCount: {
    fontSize: tokens.fontSize.xs,
    color: tokens.color.textSubtle,
  },
  stageEditInstruction: {
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
    color: tokens.color.textMuted,
  },
  stageOptions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: tokens.spacing.sm,
  },
  stageOptionButton: {
    minHeight: 44,
    borderWidth: 1,
    borderColor: tokens.color.border,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.md,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.color.surface,
  },
  stageOptionButtonCurrent: {
    backgroundColor: tokens.color.accentSoft,
    borderColor: tokens.color.accent,
  },
  stageOptionButtonText: {
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
    color: tokens.color.text,
    fontWeight: "500",
  },
  disabledControl: {
    opacity: 0.5,
  },
  focusedControl: {
    borderWidth: 2,
    borderColor: tokens.color.accent,
  },
  dateGroup: {
    gap: tokens.spacing.sm,
  },
  dateHeader: {
    flexDirection: "row",
    alignItems: "baseline",
    flexWrap: "wrap",
    gap: tokens.spacing.sm,
  },
  dateTitle: {
    fontSize: tokens.fontSize.sm,
    fontWeight: "500",
    color: tokens.color.text,
  },
  dateCount: {
    fontSize: tokens.fontSize.xs,
    color: tokens.color.textSubtle,
  },
  imageGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: tokens.spacing.md,
  },
});

export default PatientImagesView;
